import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { storage } from '../utils/storage'
import { Platform } from '../utils/platform'
import { supabase } from '../services/supabase'
import { useAuthStore } from './useAuthStore'
import uuid from 'react-native-uuid'

// Get the appropriate storage implementation
const getStorage = () => {
  if (Platform.isNative && (global as any).__FLOWS_STORAGE__) {
    return (global as any).__FLOWS_STORAGE__;
  }
  return storage;
};

export interface Notification {
  id: string
  user_id: string
  title: string
  message: string
  type: 'info' | 'success' | 'warning' | 'error'
  is_read: boolean
  action_url?: string
  created_at: string
  updated_at: string
  data?: Record<string, any>
}

interface NotificationStore {
  notifications: Notification[]
  unreadCount: number
  isLoading: boolean
  error: string | null
  hasInitialized: boolean
  syncNotifications: () => Promise<void>
  addNotification: (data: Omit<Notification, 'id' | 'user_id' | 'is_read' | 'created_at' | 'updated_at'>) => Promise<string | undefined>
  markAsRead: (id: string) => Promise<void>
  markAllAsRead: () => Promise<void>
  removeNotification: (id: string) => Promise<void>
  clearAll: () => Promise<void>
}

export const useNotificationStore = create<NotificationStore>()(
  persist(
    (set) => ({
      notifications: [],
      unreadCount: 0,
      isLoading: false,
      error: null,
      hasInitialized: false,

      syncNotifications: async () => {
        const userId = useAuthStore.getState()?.user?.id
        if (!userId) {
          return
        }

        try {
          set({ isLoading: true, error: null })

          const { data: notifications, error } = await supabase
            .from('notifications')
            .select('*')
            .eq('user_id', userId)
            .order('created_at', { ascending: false })
            .limit(100)

          if (error) throw error

          const unreadCount = (notifications || []).filter(n => !n.is_read).length

          set({
            notifications: notifications || [],
            unreadCount,
            isLoading: false,
            hasInitialized: true
          })
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to sync notifications',
            isLoading: false,
            hasInitialized: true
          })
        }
      },

      addNotification: async (data) => {
        const userId = useAuthStore.getState()?.user?.id
        if (!userId) {
          return
        }

        try {
          const newNotification = {
            id: uuid.v4().toString(),
            user_id: userId,
            title: data.title,
            message: data.message,
            type: data.type,
            is_read: false,
            action_url: data.action_url,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            data: data.data
          }

          const { error } = await supabase
            .from('notifications')
            .insert([newNotification])

          if (error) throw error

          set(state => ({
            notifications: [newNotification, ...state.notifications],
            unreadCount: state.unreadCount + 1
          }))

          return newNotification.id
        } catch (error) {
          return undefined
        }
      },

      markAsRead: async (id: string) => {
        const userId = useAuthStore.getState()?.user?.id
        if (!userId) {
          return
        }

        try {
          const { error } = await supabase
            .from('notifications')
            .update({ is_read: true })
            .eq('id', id)
            .eq('user_id', userId)

          if (error) throw error

          set(state => ({
            notifications: state.notifications.map(n =>
              n.id === id ? { ...n, is_read: true } : n
            ),
            unreadCount: Math.max(0, state.unreadCount - 1)
          }))
        } catch (error) {
          // Error handling is done via the error state
        }
      },

      markAllAsRead: async () => {
        const userId = useAuthStore.getState()?.user?.id
        if (!userId) {
          return
        }

        try {
          const { error } = await supabase
            .from('notifications')
            .update({ is_read: true })
            .eq('user_id', userId)
            .eq('is_read', false)

          if (error) throw error

          set(state => ({
            notifications: state.notifications.map(n => ({ ...n, is_read: true })),
            unreadCount: 0
          }))
        } catch (error) {
          // Error handling is done via the error state
        }
      },

      removeNotification: async (id: string) => {
        const userId = useAuthStore.getState()?.user?.id
        if (!userId) {
          return
        }

        try {
          const { error } = await supabase
            .from('notifications')
            .delete()
            .eq('id', id)
            .eq('user_id', userId)

          if (error) throw error

          set(state => {
            const notification = state.notifications.find(n => n.id === id)
            const wasUnread = notification && !notification.is_read
            return {
              notifications: state.notifications.filter(n => n.id !== id),
              unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount
            }
          })
        } catch (error) {
          // Error handling is done via the error state
        }
      },

      clearAll: async () => {
        const userId = useAuthStore.getState()?.user?.id
        if (!userId) {
          return
        }

        try {
          const { error } = await supabase
            .from('notifications')
            .delete()
            .eq('user_id', userId)

          if (error) throw error

          set({
            notifications: [],
            unreadCount: 0
          })
        } catch (error) {
          // Error handling is done via the error state
        }
      }
    }),
    {
      name: 'flows-notifications',
      storage: createJSONStorage(() => getStorage())
    }
  )
)
